'use client';

import { driver } from 'driver.js';
import { useEffect, useRef } from 'react';

import 'driver.js/dist/driver.css';

import { useDoneTour } from '@/components/shared/components/auto-tour/services/hooks';
import { buildDriverSteps } from '@/components/shared/helpers/hooks/tour/build-driver-steps';
import {
  activateTab,
  findVisibleElement,
  getSelectedTabId,
  hasDisabledParent,
  hasFixedOrStickyParent,
  isElementVisibleStrict,
  restoreAllDisabledPositions,
  scrollToCenter,
  showPopover,
  waitForPopoverElement,
} from '@/components/shared/helpers/hooks/tour/dom';
import { lockScroll, unlockScroll } from '@/components/shared/helpers/hooks/tour/scroll-lock';
import { ensureTourStyles } from '@/components/shared/helpers/hooks/tour/styles';
import {
  ProcessedTourStep,
  TourStep,
  UseTourOptions,
} from '@/components/shared/helpers/hooks/tour/types';

import { pageLevelLocalization } from '@/constant/localization';

export type { TourStep, UseTourOptions };

export function useTour({
  id,
  steps,
  condition = true,
  options,
  delay = 0,
  onComplete,
  onDestroy,
}: UseTourOptions) {
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  const onDestroyStartedRef = useRef<(() => void) | null>(null);
  const isDestroyedRef = useRef(false);
  const scrollPositionRef = useRef<number>(0);
  const { mutate } = useDoneTour();
  const { product: productLocalization } = pageLevelLocalization;

  useEffect(() => {
    if (!condition || !steps?.length) {
      // Explicitly close an active tour when it is disabled externally (for example,
      // when the user starts a purchase flow). driver.js's own `destroy()` skips our
      // `onDestroyStarted` hook (it only runs that hook for user-initiated closes), so
      // calling it directly here would bypass the app-level cleanup: the `onDestroy`
      // callback (which marks the tour as seen and flips `tourCompleted`) would never
      // fire, and the tour would silently restart from step 1 once this condition
      // becomes true again (e.g. after the purchase popup closes). Route through the
      // same `onDestroyStarted` used for a normal close instead, guarded so it can't
      // double-fire if the tour already finished/closed on its own.
      if (driverRef.current && !isDestroyedRef.current) {
        try {
          onDestroyStartedRef.current?.();
        } catch {}
      }
      driverRef.current = null;
      onDestroyStartedRef.current = null;
      unlockScroll();
      return;
    }

    isDestroyedRef.current = false;

    // Lock scrolling when tour starts
    lockScroll(scrollPositionRef);

    // Add global CSS to control popover visibility during element preparation only
    ensureTourStyles();

    // Add initializing class to hide popover on first step
    document.body.classList.add('tour-initializing');

    // Smart arrow positioning - aligns arrow to element position relative to popover
    const adjustArrowPosition = () => {
      if (typeof window === 'undefined') return;

      const isMobile = window.innerWidth <= 768;
      if (!isMobile) return; // Only adjust on mobile

      const popover = document.querySelector('.driver-popover') as HTMLElement;
      const arrow = document.querySelector('.driver-popover-arrow') as HTMLElement;
      const highlightedElement = document.querySelector(
        '.driver-active-element',
      ) as HTMLElement;

      if (!popover || !arrow || !highlightedElement) return;

      const popoverRect = popover.getBoundingClientRect();
      const elementRect = highlightedElement.getBoundingClientRect();

      // Calculate the center of the highlighted element
      const elementCenterX = elementRect.left + elementRect.width / 2;

      // Calculate the center of the popover
      const popoverCenterX = popoverRect.left + popoverRect.width / 2;

      // Calculate where the arrow should point (element center relative to popover)
      const arrowPosition = elementCenterX - popoverRect.left;

      // Fine tune popover position for steps with tabId to prevent overlap
      const activeIndex = driverRef.current?.getActiveIndex();
      if (activeIndex !== undefined && activeIndex >= 0) {
        const currentStep = processedSteps[activeIndex];
        const originalStep = currentStep ? steps[currentStep.originalIndex] : null;
        const hasTabId = originalStep?.tabId;
        const currentTop = parseInt(popover.style.top || '0');
        if (hasTabId) {
          // Adjust popover position if step has tabId

          if (currentTop > 0 && popoverRect.top < elementRect.top) {
            // popover is above element
            popover.style.top = `${currentTop - 12}px`;
          } else if (currentTop > 0) {
            // popover is below element
            popover.style.top = `${currentTop + 12}px`;
          }
        }
      }

      // Clamp the position to stay within popover bounds (with 20px padding)
      const minPos = 20;
      const maxPos = popoverRect.width - 20;
      const clampedPosition = Math.max(minPos, Math.min(maxPos, arrowPosition));

      // Apply the position
      arrow.style.left = `${clampedPosition}px`;
      arrow.style.right = 'auto';
    };

    // (no isReloading flag needed)
    let isFirstStep = true; // Track if this is the first step
    let reloadFallbackTimer: number | null = null;
    let layoutShiftCheckInterval: NodeJS.Timeout | null = null;

    // Process steps: find visible elements and add unique identifiers
    const processedSteps: ProcessedTourStep[] = [];

    steps.forEach((step, index) => {
      // Don't activate tabs here - they will be activated when tour reaches each step
      // in the onHighlightStarted callback below
      const visibleElement = findVisibleElement(step.element, !!step.tabId);

      if (visibleElement) {
        const targetElement = visibleElement as HTMLElement;

        // Add a unique data attribute to ensure we target the correct element
        const uniqueAttr = `data-tour-target-${Date.now()}-${index}`;
        targetElement.setAttribute(uniqueAttr, 'true');

        // Update the selector to target this specific element
        const updatedStep = {
          ...step,
          element: `[${uniqueAttr}="true"]`,
          visibleElement: targetElement,
          originalIndex: index, // Store original index to map back to original steps array
        };

        processedSteps.push(updatedStep);
      }
    });

    // Don't start tour if no visible steps
    if (processedSteps.length === 0) {
      return;
    }

    let driverObj: ReturnType<typeof driver> | null = null;

    const onDestroyStarted = () => {
      if (isDestroyedRef.current) return;
      isDestroyedRef.current = true;
      try {
        // Clean up CSS classes
        document.body.classList.remove('tour-hiding');
        document.body.classList.remove('tour-initializing');

        // Unlock scrolling when tour ends
        unlockScroll();

        // Restore position of any sticky/fixed element the tour disabled on mobile
        // (see scrollToCenter / preScrollFirstElement / the non-tab positioning branch below)
        restoreAllDisabledPositions();

        const activeIndex = driverObj?.getActiveIndex();
        const isLastStep =
          typeof activeIndex === 'number' && activeIndex === processedSteps.length - 1;

        if (isLastStep && id) {
          try {
            mutate(id, {
              onError: () => {},
            });
          } catch {}
        }

        if (isLastStep && onComplete) onComplete();

        // Always call onDestroy when tour is destroyed (completed or closed)
        if (onDestroy) onDestroy();

        // cleanup attributes/placeholders
        processedSteps.forEach((step) => {
          const attrName = step.element.match(/\[(.*?)="/)?.[1];
          if (attrName && step.visibleElement) {
            try {
              step.visibleElement.removeAttribute(attrName);
            } catch {}
            if (step.visibleElement.id?.startsWith('tour-placeholder-'))
              step.visibleElement.remove();
          }
        });

        try {
          driverObj?.destroy();
        } catch {}
      } catch {
        // ignore
      }
    };

    onDestroyStartedRef.current = onDestroyStarted;

    const onDeselected = (element?: Element | null) => {
      // Hide popover during transition between steps to prevent flash
      document.body.classList.add('tour-hiding');

      // Reset popover and highlight animation state to prevent jump when moving between steps
      // Note: We don't hide overlay - it should stay visible throughout the tour
      try {
        const popover = document.querySelector('.driver-popover') as HTMLElement;
        const activeElement = document.querySelector('.driver-active-element') as HTMLElement;

        if (popover) {
          // Force animation reset for popover
          popover.style.transition = 'none';
          popover.style.opacity = '0';

          // Force reflow to apply changes immediately
          popover.offsetHeight;
        }

        if (activeElement) {
          // Force animation reset for highlighted element
          activeElement.style.transition = 'none';

          // Force reflow
          activeElement.offsetHeight;
        }
      } catch {
        // ignore
      }

      // For tab steps, reset them back to a "placeholder" state when we leave them
      // so that the next time we enter that step, it follows the exact same flow
      // as the very first time (placeholder -> activateTab -> real element).
      try {
        const deselectedEl = element as HTMLElement | null;

        if (deselectedEl) {
          const stepIndex = processedSteps.findIndex((s) => s.visibleElement === deselectedEl);

          if (stepIndex >= 0) {
            const step = processedSteps[stepIndex];
            const originalStep = steps[step.originalIndex];

            if (originalStep?.tabId && originalStep.element) {
              // Recreate a fresh placeholder centered in the viewport
              const originalSelector =
                originalStep.element.startsWith('#') || originalStep.element.startsWith('.')
                  ? originalStep.element
                  : `#${originalStep.element}`;

              const placeholder = findVisibleElement(originalSelector, true);

              // Move the unique tour attribute back from the real element to the new placeholder
              const attrName = step.element.match(/\[(.*?)="/)?.[1];

              if (placeholder && attrName) {
                if (step.visibleElement && step.visibleElement !== placeholder) {
                  try {
                    (step.visibleElement as HTMLElement).removeAttribute(attrName);
                  } catch {}
                }

                try {
                  (placeholder as HTMLElement).setAttribute(attrName, 'true');
                } catch {}

                // Update step to point back to the placeholder,
                // exactly like in the initial processing phase
                step.visibleElement = placeholder;
                step.element = `[${attrName}="true"]`;
              }
            }
          }
        }
      } catch {
        // ignore
      }

      // Don't remove placeholders here - they need to persist for future tab steps
      // Placeholders are cleaned up individually when replaced (line ~440) and
      // collectively when tour ends (in onDestroyStarted)
    };

    // onHighlightStarted: handle highlight events from driver.js. We use a reloadSet to
    // coordinate placeholder -> real element swaps for tab steps so we don't flicker or jump.
    const reloadSet = new Set<number>();
    let handlingStep = false;

    // Steps without a `tabId` can still live inside a tab panel (e.g. the default description
    // tab). Remember which tab each was first shown under (keyed by original step index) so
    // "Previous" can switch back to it after a later tab step moved away.
    const stepTabContext = new Map<number, string>();
    const tourTabIds = steps.map((s) => s.tabId).filter(Boolean) as string[];

    const onHighlightStarted = async () => {
      if (handlingStep) return; // guard re-entrancy
      handlingStep = true;
      try {
        // Get current active index immediately
        const activeIndex = driverObj?.getActiveIndex();
        if (activeIndex === undefined || activeIndex < 0) return;

        // Check if this is the first step
        const isCurrentStepFirst = activeIndex === 0;

        // Check if current step has tabId - if yes, hide popover immediately to prevent flash
        const currentStep = processedSteps[activeIndex];
        const originalStep = currentStep ? steps[currentStep.originalIndex] : null;
        const hasTabId = originalStep?.tabId;

        let revisitTabId: string | null = null;
        if (currentStep && originalStep?.element && !hasTabId && tourTabIds.length > 0) {
          const stepSelector =
            originalStep.element.startsWith('#') || originalStep.element.startsWith('.')
              ? originalStep.element
              : `#${originalStep.element}`;
          const isTargetVisible = Array.from(
            document.querySelectorAll<HTMLElement>(stepSelector),
          ).some((el) => isElementVisibleStrict(el));

          if (isTargetVisible) {
            if (!stepTabContext.has(currentStep.originalIndex)) {
              const selectedTabId = getSelectedTabId(tourTabIds);
              if (selectedTabId) stepTabContext.set(currentStep.originalIndex, selectedTabId);
            }
          } else {
            revisitTabId = stepTabContext.get(currentStep.originalIndex) ?? null;
          }
        }

        if (hasTabId || revisitTabId) {
          // Immediately hide popover for tab steps to prevent flash of previous step
          document.body.classList.add('tour-hiding');
        }

        // For first step only, hide popover to prevent flash during initial positioning
        if (isCurrentStepFirst && isFirstStep) {
          document.body.classList.add('tour-initializing');
        }

        // If this is a reload highlight after we swapped placeholder -> real element
        if (reloadSet.has(activeIndex)) {
          reloadSet.delete(activeIndex);
          // Clear any fallback timer
          try {
            if (reloadFallbackTimer) {
              clearTimeout(reloadFallbackTimer);
              reloadFallbackTimer = null;
            }
          } catch {}

          // Wait for driver.js to attach to real element
          await new Promise((r) => setTimeout(r, 100));

          // Force update events to help driver.js sync with layout
          if (window.innerWidth <= 768) {
            window.dispatchEvent(new Event('resize'));
            window.dispatchEvent(new Event('scroll'));
          }

          // Multiple refreshes for accurate positioning before showing
          try {
            driverObj?.refresh();
          } catch {}

          await new Promise((r) => setTimeout(r, 100));

          try {
            driverObj?.refresh();
          } catch {}

          await new Promise((r) => setTimeout(r, 100));

          // Final refresh for precise positioning
          try {
            driverObj?.refresh();
          } catch {}

          await new Promise((r) => setTimeout(r, 75));

          // Remove all hiding classes and show the popover - let driver.js animation handle the fade-in
          document.body.classList.remove('tour-hiding');
          document.body.classList.remove('tour-initializing');

          // Small delay to ensure popover is positioned before becoming visible
          await new Promise((r) => setTimeout(r, 50));

          const pop = await waitForPopoverElement(1000);
          if (pop) {
            showPopover(pop, adjustArrowPosition);
          }
          if (isCurrentStepFirst) isFirstStep = false;
          return;
        }

        // Normal highlight processing for the active step
        const currentIndex = activeIndex;
        // currentStep and originalStep already defined above
        // Use originalIndex to get the correct element from original steps array
        // This handles cases where some steps are filtered out (e.g., on mobile)
        const originalElement = currentStep ? steps[currentStep.originalIndex]?.element : null;

        // ensure there's a popover element reference (may be created asynchronously)
        let popover =
          document.querySelector('.driver-popover') || (await waitForPopoverElement(1000));

        if (!originalElement) {
          document.body.classList.remove('tour-hiding');
          document.body.classList.remove('tour-initializing');
          if (popover) showPopover(popover, adjustArrowPosition);
          if (isCurrentStepFirst) isFirstStep = false;
          return;
        }

        const elementSelector =
          originalElement.startsWith('#') || originalElement.startsWith('.')
            ? originalElement
            : `#${originalElement}`;

        // Check if visibleElement still exists in DOM (important when going back from tab steps)
        // If element doesn't exist or is a placeholder, find the real element again
        const uniqueAttr = currentStep.element.match(/\[(.*?)="/)?.[1];

        const needsElementRefresh =
          !currentStep.visibleElement ||
          !document.body.contains(currentStep.visibleElement) ||
          currentStep.visibleElement.id?.startsWith('tour-placeholder-') ||
          !isElementVisibleStrict(currentStep.visibleElement as HTMLElement);

        // Set when the step's target attribute is moved onto a freshly found element. driver.js
        // resolved its selector before that (to nothing, so it anchored to its dummy element),
        // so it has to be told to re-highlight or the popover stays centered on the screen.
        let elementSwapped = false;

        if (needsElementRefresh && uniqueAttr && !revisitTabId) {
          // Find the real element again - try multiple selectors with retry
          let realElement: HTMLElement | null = null;

          // Try up to 5 times with delays to ensure element is available
          for (let attempt = 0; attempt < 5 && !realElement; attempt++) {
            if (attempt > 0) {
              await new Promise((r) => setTimeout(r, 100));
            }

            const elements = document.querySelectorAll<HTMLElement>(elementSelector);
            for (let i = 0; i < elements.length; i++) {
              const el = elements[i];
              if (isElementVisibleStrict(el)) {
                realElement = el as HTMLElement;
                break;
              }
            }
          }

          if (realElement) {
            // Clean up old element
            try {
              if (currentStep.visibleElement) {
                currentStep.visibleElement.removeAttribute(uniqueAttr);
                if (currentStep.visibleElement.id?.startsWith('tour-placeholder-')) {
                  currentStep.visibleElement.remove();
                }
              }
            } catch {}

            // Update to new element
            try {
              realElement.setAttribute(uniqueAttr, 'true');
              if (realElement !== currentStep.visibleElement) elementSwapped = true;
              currentStep.visibleElement = realElement;
            } catch {}

            // Wait a bit for element to be ready
            await new Promise((r) => setTimeout(r, 50));

            // Force driver to re-evaluate the element
            try {
              driverObj?.refresh();
              await new Promise((r) => setTimeout(r, 25));
            } catch {}
          }
        }

        // If step belongs to a tab, activate tab and wait for the real element
        const stepTabId = currentStep?.tabId ?? revisitTabId;
        if (stepTabId) {
          // Hide popover during tab activation and scrolling
          // For first step, we already have tour-initializing, so hiding is redundant but doesn't hurt
          if (!document.body.classList.contains('tour-initializing')) {
            document.body.classList.add('tour-hiding');
          }

          await activateTab(stepTabId);

          // Wait for tab content to render (retry loop)
          let realElement: HTMLElement | null = null;
          const start = Date.now();
          const maxWait = 2500;
          while (Date.now() - start < maxWait) {
            realElement = document.querySelector(elementSelector) as HTMLElement | null;
            if (realElement) break;
            await new Promise((r) => setTimeout(r, 100));
          }

          if (!realElement) {
            // couldn't find real element; just show popover to avoid leaving user stuck
            document.body.classList.remove('tour-hiding');
            document.body.classList.remove('tour-initializing');
            if (popover) showPopover(popover, adjustArrowPosition);
            if (isCurrentStepFirst) isFirstStep = false;
            return;
          }

          // Transfer unique attribute to the real element (works for both placeholder and when going back)
          const uniqueAttr = currentStep.element.match(/\[(.*?)="/)?.[1];
          if (uniqueAttr) {
            // Check if current visibleElement is a placeholder
            const isPlaceholder =
              currentStep.visibleElement?.hasAttribute('data-tour-placeholder');

            // If it's a placeholder or the real element is not in DOM (when going back), update it
            if (isPlaceholder || !document.body.contains(currentStep.visibleElement)) {
              const oldElement = currentStep.visibleElement as HTMLElement;
              try {
                // Remove attribute from old element
                oldElement?.removeAttribute(uniqueAttr);
              } catch {}
              try {
                // Add attribute to new real element
                realElement.setAttribute(uniqueAttr, 'true');
              } catch {}
              // Update the reference
              currentStep.visibleElement = realElement;
              currentStep.element = `[${uniqueAttr}="true"]`;
              try {
                // Clean up placeholder if exists
                if (oldElement?.id?.startsWith('tour-placeholder-')) oldElement.remove();
              } catch {}
            }
          }

          // Scroll tab element to viewport (with extra offset for tab elements)
          await scrollToCenter(realElement, true);

          // Wait for scroll to complete
          await new Promise((r) => setTimeout(r, 100));

          // Verify element is centered (for all tab steps on mobile) - multiple attempts
          const isMobile = window.innerWidth <= 768;
          if (isMobile) {
            // Check if element or parent was originally fixed/sticky and disabled
            const wasStickyDisabled = hasDisabledParent(realElement);

            // Skip verification for elements that were fixed/sticky
            // (they are now static and positioned correctly, no need to verify)
            if (!wasStickyDisabled) {
              // Try up to 3 times to center the element properly
              for (let attempt = 0; attempt < 3; attempt++) {
                const elementRect = realElement.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const elementCenter = elementRect.top + elementRect.height / 2;
                const viewportCenter = viewportHeight / 2;
                const offset = Math.abs(elementCenter - viewportCenter);

                // If not centered enough (more than 50px off), scroll again
                if (offset > 50) {
                  await scrollToCenter(realElement, true);
                  await new Promise((r) => setTimeout(r, 75));
                } else {
                  // Successfully centered, break the loop
                  break;
                }
              }
            }
          }

          // Let driver recalculate and then ask it to moveTo the same index so overlay attaches to real element
          reloadSet.add(currentIndex);

          // Multiple refreshes for accurate positioning in mobile
          try {
            driverObj?.refresh();
          } catch {}

          await new Promise((r) => setTimeout(r, 50));

          try {
            driverObj?.refresh();
          } catch {}

          await new Promise((r) => setTimeout(r, 50));

          try {
            driverObj?.moveTo(currentIndex);
          } catch {}

          // Wait for moveTo to complete and recalculate position
          await new Promise((r) => setTimeout(r, 100));

          // Force more refreshes for mobile tab elements (isMobile already defined above)
          if (isMobile) {
            try {
              driverObj?.refresh();
            } catch {}

            await new Promise((r) => setTimeout(r, 50));

            try {
              driverObj?.refresh();
            } catch {}

            await new Promise((r) => setTimeout(r, 50));
          }

          // Fallback timeout
          try {
            if (reloadFallbackTimer) {
              clearTimeout(reloadFallbackTimer);
              reloadFallbackTimer = null;
            }
            const idx = currentIndex;
            const fallbackDelay = 400; // Increased for more reliable positioning
            reloadFallbackTimer = window.setTimeout(async () => {
              if (!reloadSet.has(idx)) return;
              reloadSet.delete(idx);
              document.body.classList.remove('tour-hiding');
              document.body.classList.remove('tour-initializing');
              const p = await waitForPopoverElement(1000);
              if (p) showPopover(p, adjustArrowPosition);
            }, fallbackDelay) as unknown as number;
          } catch {}

          if (isCurrentStepFirst) isFirstStep = false;
          return;
        }

        // Non-tab steps: ensure target in viewport, refresh and show popover
        let targetElement = document.querySelector(elementSelector) as HTMLElement | null;

        // If element not found, try using the unique attribute selector
        if (!targetElement && uniqueAttr) {
          targetElement = document.querySelector(
            `[${uniqueAttr}="true"]`,
          ) as HTMLElement | null;
        }

        // If element found but not visible, or not found at all, try finding it again
        if (
          (!targetElement || !isElementVisibleStrict(targetElement)) &&
          !originalStep?.tabId
        ) {
          const originalSelector =
            originalElement.startsWith('#') || originalElement.startsWith('.')
              ? originalElement
              : `#${originalElement}`;

          // Try to find visible element (like we do at initialization)
          const elements = document.querySelectorAll<HTMLElement>(originalSelector);
          let foundVisible = false;

          for (let i = 0; i < elements.length; i++) {
            const element = elements[i] as HTMLElement;
            if (isElementVisibleStrict(element)) {
              targetElement = element;
              foundVisible = true;
              break;
            }
          }

          // If still not found, retry up to 3 times with small delays (element might be loading)
          if (!foundVisible) {
            for (let retry = 0; retry < 3; retry++) {
              await new Promise((r) => setTimeout(r, 50));
              const retryElements = document.querySelectorAll<HTMLElement>(originalSelector);
              for (let i = 0; i < retryElements.length; i++) {
                const element = retryElements[i];
                if (isElementVisibleStrict(element)) {
                  targetElement = element;
                  foundVisible = true;
                  break;
                }
              }
              if (foundVisible) break;
            }
          }

          // If found now, update the attribute
          if (targetElement && uniqueAttr) {
            try {
              targetElement.setAttribute(uniqueAttr, 'true');
              if (targetElement !== currentStep.visibleElement) elementSwapped = true;
              currentStep.visibleElement = targetElement;

              // Wait for element to be ready
              await new Promise((r) => setTimeout(r, 50));

              // Force driver to re-evaluate the element
              try {
                driverObj?.refresh();
                await new Promise((r) => setTimeout(r, 25));
              } catch {}
            } catch {}
          }
        }

        if (!targetElement) {
          // Element not found - try forcing a moveTo to current index to retry
          try {
            driverObj?.refresh();
            await new Promise((r) => setTimeout(r, 50));
            driverObj?.moveTo(currentIndex);
            await new Promise((r) => setTimeout(r, 100));
          } catch {}

          // Remove all hiding classes and show popover anyway to prevent user from getting stuck
          document.body.classList.remove('tour-hiding');
          document.body.classList.remove('tour-initializing');
          const finalPopover = document.querySelector('.driver-popover') || popover;
          if (finalPopover) {
            showPopover(finalPopover, adjustArrowPosition);
          }
          if (isCurrentStepFirst) isFirstStep = false;
          return;
        }

        // Element found - proceed with positioning

        // Check if element needs special handling (sticky/fixed on mobile)
        const isMobileDevice = window.innerWidth <= 768;
        let needsManualScroll = false;

        if (isMobileDevice) {
          const parentCheck = hasFixedOrStickyParent(targetElement);
          const computedStyle = window.getComputedStyle(targetElement);
          const position = computedStyle.position;
          const elementIsSticky = position === 'sticky' || position === '-webkit-sticky';
          const elementIsFixed = position === 'fixed';

          const isSticky = elementIsSticky || parentCheck.isSticky;
          const isFixed = elementIsFixed || parentCheck.isFixed;
          const fixedParent = parentCheck.fixedParent;

          // Check if NOT already disabled
          const alreadyDisabled = hasDisabledParent(targetElement);

          if ((isSticky || isFixed) && !alreadyDisabled) {
            needsManualScroll = true; // Need manual scroll for sticky/fixed elements
            const elementToDisable = fixedParent || targetElement;

            // Store original values and disable ONLY if not already done
            if (!elementToDisable.hasAttribute('data-tour-original-position-inline')) {
              elementToDisable.setAttribute(
                'data-tour-original-position-inline',
                elementToDisable.style.position || '',
              );
              elementToDisable.setAttribute(
                'data-tour-original-top-inline',
                elementToDisable.style.top || '',
              );
              elementToDisable.setAttribute(
                'data-tour-original-bottom-inline',
                elementToDisable.style.bottom || '',
              );

              // Disable sticky/fixed
              elementToDisable.style.setProperty('position', 'static', 'important');

              // Force reflow to ensure layout is stable
              targetElement.offsetHeight;

              // Wait for layout to settle
              await new Promise((r) => setTimeout(r, 75));
            }
          }
        }

        // Only scroll manually if needed (sticky/fixed elements on mobile)
        if (needsManualScroll) {
          await scrollToCenter(targetElement, false);
          await new Promise((r) => setTimeout(r, 75));

          // Verify element is centered - single attempt
          const elementRect = targetElement.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const elementCenter = elementRect.top + elementRect.height / 2;
          const viewportCenter = viewportHeight / 2;
          const offset = Math.abs(elementCenter - viewportCenter);

          // If not centered enough, scroll once more
          if (offset > 50) {
            await scrollToCenter(targetElement, false);
            await new Promise((r) => setTimeout(r, 50));
          }
        }

        if (elementSwapped) {
          // Re-highlight the same index so driver.js re-resolves the step selector (and scrolls
          // to the element) instead of staying on its dummy element. The nested
          // onHighlightStarted call is ignored by the handlingStep guard.
          try {
            driverObj?.moveTo(currentIndex);
          } catch {}
        }

        // Refresh to let driver.js know about final position
        try {
          driverObj?.refresh();
        } catch {}

        await new Promise((r) => setTimeout(r, 50));

        // For non-tab steps, remove hiding immediately and let driver.js animate naturally
        document.body.classList.remove('tour-hiding');
        document.body.classList.remove('tour-initializing');

        const finalPopover = document.querySelector('.driver-popover') || popover;
        if (finalPopover) {
          showPopover(finalPopover, adjustArrowPosition);
        }
        if (isCurrentStepFirst) isFirstStep = false;
      } finally {
        handlingStep = false;
      }
    };

    // Initialize the tour driver with full popover config so styling and onPopoverRender run
    try {
      const stepsForDriver = buildDriverSteps(processedSteps, steps, productLocalization);

      driverObj = driver({
        animate: true, // Use driver.js animations
        opacity: 0.4,
        padding: 0,
        allowClose: true,
        allowKeyboardControl: true,
        disableActiveInteraction: false,
        stagePadding: 0,
        stageRadius: 4,
        overlayClickNext: false,
        showButtons: true,
        // Optimize popover positioning speed
        popoverOffset: 10,
        smoothScroll: false, // We handle scrolling ourselves
        onDeselected,
        onHighlightStarted,
        onDestroyStarted,
        steps: stepsForDriver,
        ...options,
      });

      driverRef.current = driverObj;

      // Pre-scroll first element to center before starting tour
      // This handles layout shifts from lazy-loaded images/banners
      const preScrollFirstElement = async () => {
        if (processedSteps.length === 0) return;

        const firstStep = processedSteps[0];
        const firstElement = firstStep.visibleElement as HTMLElement;

        // Don't scroll placeholder elements
        if (!firstElement || firstElement.id?.startsWith('tour-placeholder-')) {
          return;
        }

        // Check if element has fixed/sticky parent BEFORE scrolling
        const parentCheck = hasFixedOrStickyParent(firstElement);

        // If element has fixed parent, just disable it and skip all scrolling
        if (parentCheck.isFixed && parentCheck.fixedParent) {
          const isMobile = window.innerWidth <= 768;
          if (isMobile) {
            // Disable the fixed parent ONLY if not already done
            if (!parentCheck.fixedParent.hasAttribute('data-tour-original-position-inline')) {
              parentCheck.fixedParent.setAttribute(
                'data-tour-original-position-inline',
                parentCheck.fixedParent.style.position || '',
              );
              parentCheck.fixedParent.setAttribute(
                'data-tour-original-top-inline',
                parentCheck.fixedParent.style.top || '',
              );
              parentCheck.fixedParent.setAttribute(
                'data-tour-original-bottom-inline',
                parentCheck.fixedParent.style.bottom || '',
              );

              // Only change position if we just stored the original
              parentCheck.fixedParent.style.setProperty('position', 'static', 'important');
            }
          }
          return; // Skip all scrolling for fixed parent elements
        }

        // Scroll first element to center before tour starts
        await scrollToCenter(firstElement, false);

        // Wait for potential layout shifts (images/banners loading)
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
          await new Promise((r) => setTimeout(r, 150));

          // Check if element or parent was originally fixed/sticky and disabled
          const wasStickyDisabled = hasDisabledParent(firstElement);

          // Skip verification for elements that were fixed/sticky
          // (they are now static and positioned correctly, no need to verify)
          if (!wasStickyDisabled) {
            // Try multiple times to ensure element is centered (up to 3 attempts)
            for (let attempt = 0; attempt < 3; attempt++) {
              const elementRect = firstElement.getBoundingClientRect();
              const viewportHeight = window.innerHeight;
              const elementCenter = elementRect.top + elementRect.height / 2;
              const viewportCenter = viewportHeight / 2;
              const offset = Math.abs(elementCenter - viewportCenter);

              // If element moved more than 50px from center, re-scroll
              if (offset > 50) {
                await scrollToCenter(firstElement, false);
                await new Promise((r) => setTimeout(r, 100));
              } else {
                // Successfully centered
                break;
              }
            }
          }
        } else {
          // For desktop, shorter wait
          await new Promise((r) => setTimeout(r, 50));
        }
      };

      const startTimer = setTimeout(async () => {
        try {
          // Pre-scroll first element on mobile
          await preScrollFirstElement();

          // Set up layout shift observer for first step (mobile only)
          const isMobile = window.innerWidth <= 768;
          if (isMobile && processedSteps.length > 0) {
            const firstElement = processedSteps[0].visibleElement as HTMLElement;

            // Don't set up shift observer for sticky/fixed elements (they're static now)
            const wasStickyDisabled = firstElement ? hasDisabledParent(firstElement) : false;

            if (
              firstElement &&
              !firstElement.id?.startsWith('tour-placeholder-') &&
              !wasStickyDisabled
            ) {
              let lastTop = firstElement.getBoundingClientRect().top;
              let checkCount = 0;
              const maxChecks = 10; // Check for 5 seconds (10 * 500ms)

              layoutShiftCheckInterval = setInterval(async () => {
                checkCount++;

                // Stop after max checks or when first step is done
                if (checkCount >= maxChecks || !isFirstStep) {
                  if (layoutShiftCheckInterval) {
                    clearInterval(layoutShiftCheckInterval);
                    layoutShiftCheckInterval = null;
                  }
                  return;
                }

                const currentRect = firstElement.getBoundingClientRect();
                const shift = Math.abs(currentRect.top - lastTop);

                // If element shifted more than 50px, re-center it
                if (shift > 50) {
                  const viewportHeight = window.innerHeight;
                  const elementCenter = currentRect.top + currentRect.height / 2;
                  const viewportCenter = viewportHeight / 2;

                  // Only re-scroll if not already centered
                  if (Math.abs(elementCenter - viewportCenter) > 80) {
                    await scrollToCenter(firstElement, false);

                    // Refresh driver to update overlay position
                    try {
                      driverObj?.refresh();
                    } catch {}
                  }
                }

                lastTop = currentRect.top;
              }, 250) as any;
            }
          }

          driverObj?.drive();
        } catch {}
      }, delay);

      // Initial show timer - removed as driver.js handles animation now
      // Steps will be shown by onHighlightStarted after positioning
      let showTimer: NodeJS.Timeout | null = null;

      return () => {
        try {
          clearTimeout(startTimer);
        } catch {}
        try {
          if (showTimer) clearTimeout(showTimer);
        } catch {}
        try {
          if (layoutShiftCheckInterval) {
            clearInterval(layoutShiftCheckInterval);
            layoutShiftCheckInterval = null;
          }
        } catch {}
        try {
          if (reloadFallbackTimer) {
            clearTimeout(reloadFallbackTimer);
            reloadFallbackTimer = null;
          }
        } catch {}
        // Clean up CSS classes
        try {
          document.body.classList.remove('tour-hiding');
          document.body.classList.remove('tour-initializing');
        } catch {}
        // Unlock scroll on cleanup
        try {
          unlockScroll();
        } catch {}
        // Restore position of any sticky/fixed element the tour disabled on mobile
        try {
          restoreAllDisabledPositions();
        } catch {}
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[tour] driver initialization error:', e);
      // Unlock scroll on error too
      try {
        unlockScroll();
      } catch {}
      return () => {
        try {
          if (layoutShiftCheckInterval) {
            clearInterval(layoutShiftCheckInterval);
            layoutShiftCheckInterval = null;
          }
        } catch {}
        try {
          if (reloadFallbackTimer) {
            clearTimeout(reloadFallbackTimer);
            reloadFallbackTimer = null;
          }
        } catch {}
        try {
          unlockScroll();
        } catch {}
        try {
          restoreAllDisabledPositions();
        } catch {}
      };
    }
  }, [condition, steps, options, delay, onComplete]);
}
